/**
 * Tab Context Utilities
 * 
 * This module provides per-tab workspace context management to allow
 * independent workspace sessions across multiple browser tabs.
 * 
 * Key features:
 * - Each tab maintains its own workspace context in sessionStorage
 * - Tabs can be logged into different workspaces independently
 * - Logout in one tab doesn't affect other tabs
 * - Cross-tab communication via BroadcastChannel for optional coordination
 */

/**
 * Session storage keys
 */
const TAB_SESSION_ID_KEY = "tabSessionId";
const WORKSPACE_CONTEXT_KEY = "workspaceContext";
const AUTH_USER_ID_KEY = "authUserId";
const TAB_LOGGED_OUT_KEY = "tabLoggedOut";

/**
 * BroadcastChannel for cross-tab communication
 */
let broadcastChannel: BroadcastChannel | null = null;

/**
 * Initialize BroadcastChannel if available
 */
const initBroadcastChannel = (): void => {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    return;
  }
  
  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel("budget-trace-tabs");
  }
};

/**
 * Generate a unique tab session ID
 */
const generateTabSessionId = (): string => {
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get or create tab session ID
 */
export const getTabSessionId = (): string => {
  if (typeof window === "undefined") return "";
  
  let sessionId = sessionStorage.getItem(TAB_SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = generateTabSessionId();
    sessionStorage.setItem(TAB_SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

/**
 * Workspace context stored per tab
 */
export interface WorkspaceContext {
  workspaceId: string;
  workspaceName: string;
  workspaceLogoUrl?: string;
  userId: string;
  timestamp: number;
}

/**
 * Save workspace context for current tab
 */
export const saveWorkspaceContext = (context: WorkspaceContext): void => {
  if (typeof window === "undefined") return;
  
  try {
    sessionStorage.setItem(WORKSPACE_CONTEXT_KEY, JSON.stringify(context));
    sessionStorage.setItem(AUTH_USER_ID_KEY, context.userId);
  } catch (error) {
    console.error("Error saving workspace context:", error);
  }
};

/**
 * Get workspace context for current tab
 */
export const getWorkspaceContext = (): WorkspaceContext | null => {
  if (typeof window === "undefined") return null;
  
  try {
    const contextStr = sessionStorage.getItem(WORKSPACE_CONTEXT_KEY);
    if (!contextStr) return null;
    
    const context = JSON.parse(contextStr) as WorkspaceContext;
    return context;
  } catch (error) {
    console.error("Error reading workspace context:", error);
    return null;
  }
};

/**
 * Clear workspace context for current tab
 */
export const clearWorkspaceContext = (): void => {
  if (typeof window === "undefined") return;
  
  sessionStorage.removeItem(WORKSPACE_CONTEXT_KEY);
  sessionStorage.removeItem(AUTH_USER_ID_KEY);
  sessionStorage.setItem(TAB_LOGGED_OUT_KEY, "true");
};

/**
 * Check if current tab is marked as logged out
 */
export const isTabLoggedOut = (): boolean => {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(TAB_LOGGED_OUT_KEY) === "true";
};

/**
 * Clear the logged out flag for current tab (when user logs in)
 */
export const clearTabLoggedOutFlag = (): void => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(TAB_LOGGED_OUT_KEY);
};

/**
 * Check if current tab has a workspace context
 */
export const hasWorkspaceContext = (): boolean => {
  return getWorkspaceContext() !== null;
};

/**
 * Get stored auth user ID for current tab
 */
export const getStoredAuthUserId = (): string | null => {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(AUTH_USER_ID_KEY);
};

/**
 * Broadcast events to other tabs
 */
export type TabEvent = 
  | { type: "logout"; tabSessionId: string; workspaceId: string; userId: string }
  | { type: "workspace_switch"; tabSessionId: string; workspaceId: string; userId: string };

/**
 * Send event to other tabs
 */
export const broadcastToTabs = (event: TabEvent): void => {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    return;
  }
  
  initBroadcastChannel();
  if (broadcastChannel) {
    broadcastChannel.postMessage(event);
  }
};

/**
 * Listen to events from other tabs
 */
export const listenToTabEvents = (
  callback: (event: TabEvent) => void
): (() => void) => {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    return () => {}; // Return no-op cleanup
  }
  
  initBroadcastChannel();
  
  if (!broadcastChannel) {
    return () => {}; // Return no-op cleanup
  }
  
  const handler = (event: MessageEvent<TabEvent>) => {
    // Ignore events from the same tab
    if (event.data.tabSessionId === getTabSessionId()) {
      return;
    }
    callback(event.data);
  };
  
  broadcastChannel.addEventListener("message", handler);
  
  // Return cleanup function
  return () => {
    if (broadcastChannel) {
      broadcastChannel.removeEventListener("message", handler);
    }
  };
};

/**
 * Check if a workspace context is still valid
 * (e.g., not expired, matches current user)
 */
export const isWorkspaceContextValid = (
  context: WorkspaceContext | null,
  currentUserId: string | null
): boolean => {
  if (!context) return false;
  if (!currentUserId) return false;
  
  // Check if context matches current user
  if (context.userId !== currentUserId) {
    return false;
  }
  
  // Context is valid if it's less than 24 hours old
  const maxAge = 24 * 60 * 60 * 1000; // 24 hours
  const age = Date.now() - context.timestamp;
  
  return age < maxAge;
};
